import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { userActions } from "store-redux/user-slice";
import { modalActions } from "store-redux/modal-slice";
import { API_BASE_URL, APP_PREFIX_PATH } from "configs/AppConfig";
import useInput from "hooks/useInput";
import { notEmptyIsValid } from "validators/authValidation";
import InputWithLabel from "components/UI/forms/input/InputWithLabel";
import Button from "components/UI/button/Button";
import MailIcon from "components/UI/icons/MailIcon";
import PasswordIcon from "components/UI/icons/PasswordIcon";
import PasswordEyeIcon from "components/UI/icons/PasswordEyeIcon";
import PasswordEyeOffIcon from "components/UI/icons/PasswordEyeOffIcon";
import CheckboxInput from "components/UI/forms/input/CheckboxInput";
import ErrorLabel from "components/UI/forms/errors/ErrorLabel";
import LoadingSpinner from "components/UI/loading-spinner/LoadingSpinner";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Form, Input, message, Modal, Radio } from "antd";
import { Mail, Phone, Lock } from "lucide-react";
import useWindowDimensions from "hooks/useWindowDimensions";
import useWindowWidth from "helpers/useWindowWidth";
import classes from "./SignInForm.module.css";

const rules = {
  contact: [
    { required: true, message: "Please input user contact!" },
    {
      max: 13,
      min: 13,
      message: "Must be valid contact number 10 digits",
    },
  ],
};

const SignInForm = ({ redirect = false }) => {
  const [authError, setAuthError] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [authinfo, setAuthinfo] = useState("");
  const [signInPhone, setSignInPhone] = useState();
  const [password, setPassword] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [verificationType, setVerificationType] = useState("email");
  const [step, setStep] = useState(1);
  const [form] = Form.useForm();
  const [authInfo, setAuthInfo] = useState();
  const [otpLoading, setOtpLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loginWithPhone, setLoginWithPhone] = useState(false); // Added for phone login toggle

  const dispatch = useDispatch();
  let navigate = useNavigate();
  const location = useLocation();
  const width = useWindowWidth();

  const resetPasswordClickHandler = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setVerificationType('email')
    setIsModalVisible(false);
    setStep(1);
    form.resetFields();
  };

  const sendOTP = async () => {
    setOtpLoading(true)
    try {
      const values = await form.validateFields(["email", "phone"]);
      const contactInfo =
        verificationType === "email" ? values.email : values.phone;
      setAuthInfo(contactInfo);
      const req_body = {
        type: verificationType == "phone" ? "PhoneNo" : "Email",
      };
      if (verificationType == "email") {
        req_body.email = values.email;
      } else {
        req_body.phoneNo = values.phone;
      }

      const response = await fetch(`${API_BASE_URL}auth/reset-password/request-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(req_body),
      });
  
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message ? data.message : "Failed to send OTP");
        throw new Error(data.message);
      }

      if (data?.success) {
        message.success(data?.message);
        setStep(2);
      }
    } catch (error) {
      message.error("Failed to send OTP");
    } finally {
      setOtpLoading(false)
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true)
    try {
      const values = await form.validateFields();
      const response = await fetch(`${API_BASE_URL}user/otp/reset-password/public`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          authInfo: authInfo,
          otp: values.otp,
          password: values.newPassword,
        }),
      });
  
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message);
        throw new Error(data.message);
      }

      if (data?.success) {
        message.success("Password reset successfully");
        handleCancel();
      }
    } catch (error) {
      message.error("Failed to reset password");
    } finally {
      setSubmitLoading(false)
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setAuthError(null);
    }, 2000);
  }, [authError]);

  const { isLoading, isError, mutateAsync } = useMutation(async (userData) => {
    const response = await fetch(`${API_BASE_URL}auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();
    if (!response.ok) {
      message.error(data.message ? data.message : "Invalid Email or Password");
      throw new Error(data.message);
    }

    return data;
  });

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    setAuthError(null);

    if(!loginWithPhone){
    if (!authinfo || !password) {
      message.error("Please fill Email and Password");
      return;
    }
  }

  if(loginWithPhone){
    if (!signInPhone || !password) {
      message.error("Please fill Phone Number and Password");
      return;
    }
  }

    const retrievedUser = await mutateAsync({
      authinfo: !loginWithPhone ? authinfo.trim().replace(/\s/g, "") : signInPhone.trim().replace(/\s/g, ""),
      password: password.trim().replace(/\s/g, ""),
    });

    if (retrievedUser.data.user?.type !== "CUSTOMER") {
      message.error("Invalid login type");
      return;
    }

    dispatch(userActions.login({ user: retrievedUser.data }));
    dispatch(modalActions.toggleModal({ show: false }));
    if (redirect) navigate(`/`, { replace: true });
  };

  const shiftToSignup = () => {
    if (location.pathname.includes("login")) {
      navigate("/signup");
    } else {
      dispatch(
        modalActions.changeModalComponent({
          modalType: "signup",
        })
      );
    }
  };

  return (
    <>
      <form className={classes.signinForm} onSubmit={(e) => onSubmitHandler(e)}>
        <h1>Welcome Back !</h1>
        <h2>
          Log in to Your Account and Unlock the Door to Seamless Event Planning
          and Timeless Celebrations.
        </h2>
        {isLoading && <LoadingSpinner />}
        {authError && <ErrorLabel>{authError}</ErrorLabel>}
        <div className={classes.inputField}>
          <label>{loginWithPhone ? "Phone Number" : "Email"}</label>
          {loginWithPhone ? (
            <PhoneInput
              className="phone-ant-input"
              country="IN"
              defaultCountry="IN"
              value={signInPhone}
              onChange={setSignInPhone}
              placeholder="Enter your phone number"
            />
          ) : (
            <Input
              placeholder="Type Your Email"
              className="signInInput"
              value={authinfo}
              onChange={(e) => setAuthinfo(e.target.value)}
            />
          )}
        </div>
        <div className={classes.inputField}>
          <label>Password</label>
          <Input.Password
            visibilityToggle={{
              visible: passwordVisible,
              onVisibleChange: setPasswordVisible,
            }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Type Your Password"
            className="signInInput2"
          />
        </div>
        <div className={classes.signinOptions}>
          <CheckboxInput
            id="showPassword"
            name="showPassword"
            label="Show Password"
            onClick={() => setPasswordVisible((prevState) => !prevState)}
          />
          <span
            className={classes.recoverPassword}
            onClick={resetPasswordClickHandler}
          >
            Forgot Password?
          </span>
        </div>
        <Button
          option="colored"
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          className={classes.loginButton}
          onClick={() => onSubmitHandler()}
        >
          Log In
        </Button>
        <div
          className={classes.changeLoginType}
          onClick={() => setLoginWithPhone((prevValue) => !prevValue)}
        >
          {loginWithPhone ? "Sign in with Email" : "Sign in with Phone Number"}
        </div>
        <section className={classes.signinFooter}>
          <p>
            Don't have an account ?
            <span className={classes.signUpLink} onClick={shiftToSignup}>
              Sign Up
            </span>
          </p>
        </section>
      </form>
      <Modal
        title="Reset Password"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={width > 700 ? 400 : '88vw'}
        className="reset-modal"
        closeIcon={
          <img
            src="/img/round-close.svg"
            alt="Close"
            style={{ width: 28, height: 28 }}
          />
        }
      >
        <div className={classes.modalContent}>
          <Form form={form} layout="vertical" initialValues={{ verificationType: "email" }}>
            {step === 1 && (
              <>
                <Form.Item
                  className={`${classes.formItem} otp-label`}
                  label="Verification Type"
                  name="verificationType"
                >
                  <Radio.Group
                    onChange={(e) => setVerificationType(e.target.value)}
                  >
                    <Radio.Button value="email">
                      <span className={classes.iconSpan}>
                      <Mail className="inline-block mr-1" size={16} /> Email
                      </span>
                    </Radio.Button>
                    <Radio.Button value="phone">
                      <span className={classes.iconSpan}>
                      <Phone className="inline-block mr-1" size={16} /> Phone
                      </span>
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>

                {verificationType === "email" ? (
                  <Form.Item
                    className={`${classes.formItem} otp-label`}
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      className="signInInput"
                      prefix={<Mail size={16} />}
                      placeholder="Enter your email"
                      style={{fontFamily: 'Poppins'}}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    className={classes.formItem}
                    label="Phone Number"
                    name="phone"
                    rules={rules.contact}
                  >
                    <PhoneInput
                      className="phone-ant-input"
                      country="IN"
                      defaultCountry="IN"
                    />
                    
                  </Form.Item>
                )}

                <Button
                  option="colored"
                  type="submit"
                  loading={otpLoading}
                  disabled={otpLoading}
                  className={classes.otpButton}
                  onClick={() => sendOTP()}
                >
                  Send OTP
                </Button>
              </>
            )}

            {step === 2 && (
              <div className={classes.verificationSection}>
                <Form.Item
                  className={`${classes.formItem} otp-label`}
                  label="Enter OTP"
                  name="otp"
                  rules={[
                    { required: true, message: "Please enter the OTP" },
                    {
                      pattern: /^\d{6}$/,
                      message: "Please enter a valid 6-digit OTP",
                    },
                  ]}
                >
                  <Input placeholder="Enter 6-digit OTP" maxLength={6} className="signInInput" />
                </Form.Item>

                <Form.Item
                  className={`${classes.formItem} otp-label`}
                  label="New Password"
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your new password",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters",
                    },
                  ]}
                >
                  <Input.Password
                  className="signInInput"
                    prefix={<Lock size={16} />}
                    placeholder="Enter new password"
                  />
                </Form.Item>

                <Form.Item
                  className={`${classes.formItem} otp-label`}
                  label="Confirm Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<Lock size={16} />}
                    placeholder="Confirm new password"
                    className="signInInput"
                  />
                </Form.Item>
                <Button
                  option="colored"
                  type="submit"
                  loading={submitLoading}
                  disabled={submitLoading}
                  className={classes.otpButton}
                  onClick={handleSubmit}
                >
                  Reset Password
                </Button>
              </div>
            )}
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default SignInForm;